<template>
  <div :key="componentKey" class="salary-map-container">
    <div class="relative map-wrapper">
      <StateMapLegend
        v-if="stateData.length"
        :min-salary="adjustValueForPeriod(mapStats.min_salary)"
        :max-salary="adjustValueForPeriod(mapStats.max_salary)"
      />
      <USStateMap
        :main-states="mainStates"
        :alaska-state="alaskaState"
        :hawaii-state="hawaiiState"
        :get-state-path="getStatePath"
        :get-state-color="getStateColor"
        :get-state-border="getStateBorder"
        :format-salary="formatAdjustedSalary"
        @mouse-over="handleMouseOver"
        @mouse-out="handleMouseOut"
        @touch-end="handleTouchEnd"
      />
      <StateTooltip
        v-if="hoveredState"
        :state="hoveredState"
        :formatted-salary="formatAdjustedSalary(hoveredState)"
        :position="tooltipPosition"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import useStateSalaryData from '~/composables/useStateSalaryData'
import useMapVisualization from '~/composables/useMapVisualization'
import { useGlobalSalaryPeriod } from '~/composables/useGlobalSalaryPeriod'
import { formatCurrency } from '~/utils/formatters'
import USStateMap from '~/components/map/USStateMap.vue'
import StateMapLegend from '~/components/map/StateMapLegend.vue'
import StateTooltip from '~/components/map/StateTooltip.vue'

const props = defineProps({
  permalink: {
    type: String,
    required: true
  },
  companyName: {
    type: String,
    required: true
  },
  role: {
    type: String,
    default: ''
  },
  initialData: {
    type: Object,
    default: null
  }
})
const roleRef = ref(props.role)
const componentKey = ref(0)
const { activePeriod, adjustValueForPeriod } = useGlobalSalaryPeriod()
const {
  stateData,
  mapStats,
  mainStates,
  alaskaState,
  hawaiiState
} = useStateSalaryData(props.permalink, roleRef, props.initialData)
const {
  hoveredState,
  tooltipPosition,
  getStateColor,
  getStatePath,
  getStateBorder,
  handleMouseOver,
  handleMouseOut,
  handleTouchEnd
} = useMapVisualization(mapStats)
const formatAdjustedSalary = (state) => {
  return formatCurrency(adjustValueForPeriod(state.median_salary))
}
watch(activePeriod, () => {
  componentKey.value++
})
watch(
  () => props.role,
  (newRole) => {
    roleRef.value = newRole
    componentKey.value++
  }
)
</script>

<style scoped>
.salary-map-container {
  position: relative;
  margin: 1rem 0;
  overflow: hidden;
  min-height: 400px; /* Prevent CLS during map load */
}
.map-wrapper {
  text-align: center;
  min-height: 380px; /* Reserve space for map SVG */
}
</style>
